import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const isSlowConnection = () => {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    return (
      conn.effectiveType === 'slow-2g' ||
      conn.effectiveType === '2g' ||
      (conn.downlink !== undefined && conn.downlink < 0.5)
    );
  } catch {
    return false;
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: isSlowConnection() ? 60000 : 30000,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

const TOKEN_KEY = 'token';

const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem('token_created_at', Date.now().toString());
};
const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('admin-auth-storage');
};

// Refresh token queue — prevents concurrent refresh calls
let refreshPromise = null;
const refreshSubscribers = [];

const onRefreshSuccess = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers.length = 0;
};

const onRefreshFailure = (error) => {
  refreshSubscribers.forEach((callback) => callback(null, error));
  refreshSubscribers.length = 0;
};

const subscribeToRefresh = (originalRequest, resolve, reject) => {
  refreshSubscribers.push((newToken, error) => {
    if (error) {
      reject(error);
      return;
    }
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    resolve(api(originalRequest));
  });
};

const tryRefreshToken = async () => {
  try {
    const token = getToken();
    if (!token) throw new Error('No token');

    const response = await axios.post(
      `${API_BASE_URL}/refresh`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );

    const newToken = response.data?.token;
    if (!newToken) throw new Error('No token in refresh response');

    setToken(newToken);
    return newToken;
  } catch (err) {
    clearSession();
    throw err;
  }
};

api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (isSlowConnection()) {
      config.timeout = 60000;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Don't intercept refresh requests themselves
    if (originalRequest.url === '/refresh') {
      return Promise.reject(error);
    }

    if (error.code === 'ECONNABORTED') {
      error.message = isSlowConnection()
        ? 'Request timed out. Your connection appears slow. Please try again.'
        : 'Request timed out. Please try again.';
      return Promise.reject(error);
    }

    if (!error.response) {
      if (!navigator.onLine) {
        error.message = 'You are offline. Please check your internet connection.';
      } else {
        error.message = 'Unable to connect to server. Please check if the backend is running.';
      }
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      // If already on session-expired page, just reject
      if (window.location.href.includes('session_expired=1')) {
        error.message = 'Session expired. Please login again.';
        return Promise.reject(error);
      }

      // If we already retried once, don't loop
      if (originalRequest._retry) {
        error.message = 'Session expired. Please login again.';
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // Avoid trying to refresh for login requests
      if (originalRequest.url === '/login') {
        return Promise.reject(error);
      }

      // If a refresh is already in progress, queue this request
      if (refreshPromise) {
        return new Promise((resolve, reject) => {
          subscribeToRefresh(originalRequest, resolve, reject);
        });
      }

      // Start token refresh
      refreshPromise = tryRefreshToken()
        .then((newToken) => {
          onRefreshSuccess(newToken);
          refreshPromise = null;

          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        })
        .catch(async (refreshError) => {
          onRefreshFailure(refreshError);
          refreshPromise = null;

          if (refreshError.response?.status === 401) {
            const { useAuthStore } = await import('../stores/authStore');
            const authStore = useAuthStore.getState?.();
            if (authStore?.logout) {
              authStore.logout(true);
            } else {
              window.location.href = '/admin/?session_expired=1';
            }
          }

          return Promise.reject(refreshError);
        });

      return refreshPromise;
    }

    if (error.response.status === 429) {
      error.message = 'Too many requests. Please wait a moment before trying again.';
      return Promise.reject(error);
    }

    if (error.response.status >= 500) {
      error.message = 'Server error. Please try again later.';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default api;
export { getToken };
